// src/pages/AdminSellers.js
import React, { useState, useEffect } from 'react';
import {
    Container, Row, Col, Card, Table,
    Spinner, Alert, Form, InputGroup, Button,
    Badge, Modal
} from 'react-bootstrap';
import { Search, PersonCheck, PersonDash } from 'react-bootstrap-icons';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

const AdminSellers = () => {
    const [sellers, setSellers] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [processing, setProcessing] = useState(null);
    const [showPromoteModal, setShowPromoteModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    const { t } = useTranslation();

    useEffect(() => {
        fetchSellers();
        fetchUsers();
    }, []);

    const fetchSellers = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/admin/sellers');
            setSellers(response.data.data || []);
        } catch (err) {
            setError(err.response?.data?.message || t('errors.loadingError'));
            console.error('Error fetching sellers:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await api.get('/api/admin/users');
            // Отфильтровываем пользователей, которые еще не являются селлерами
            const nonSellers = response.data.data.filter(user => !user.isSeller);
            setUsers(nonSellers);
        } catch (err) {
            console.error('Error fetching users:', err);
        }
    };

    const handleSetSellerRole = async (userId, isSeller) => {
        setProcessing(userId);

        try {
            await api.post('/api/admin/sellers/set-role', { userId, isSeller });

            if (isSeller) {
                // Если назначаем селлера, обновляем оба списка
                fetchSellers();
                fetchUsers();
            } else {
                // Если снимаем статус селлера, просто обновляем список селлеров
                setSellers(sellers.filter(seller => seller._id !== userId));
                fetchUsers();
            }
        } catch (err) {
            setError(err.response?.data?.message || t('errors.updateError'));
            console.error('Error setting seller role:', err);
        } finally {
            setProcessing(null);
            setShowPromoteModal(false);
        }
    };

    const handlePromoteClick = (user) => {
        setSelectedUser(user);
        setShowPromoteModal(true);
    };

    // Фильтруем селлеров по поисковому запросу
    const filteredSellers = sellers.filter(seller =>
        seller.username.toLowerCase().includes(search.toLowerCase())
    );

    // Форматирование даты
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString();
    };

    return (
        <Container className="my-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>{t('admin.sellers')}</h1>
            </div>

            {error && (
                <Alert variant="danger" onClose={() => setError('')} dismissible>
                    {error}
                </Alert>
            )}

            <Card className="glass-card mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">{t('admin.sellersList')}</h5>
                    <Form.Group style={{ width: '300px' }}>
                        <InputGroup>
                            <InputGroup.Text>
                                <Search />
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder={t('admin.searchSellers')}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </InputGroup>
                    </Form.Group>
                </Card.Header>
                <Card.Body>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">{t('common.loading')}</p>
                        </div>
                    ) : filteredSellers.length > 0 ? (
                        <div className="table-responsive">
                            <Table hover className="web3-table">
                                <thead>
                                <tr>
                                    <th>{t('common.user')}</th>
                                    <th>{t('referral.registrationDate')}</th>
                                    <th>{t('referral.lastLogin')}</th>
                                    <th>{t('referral.links')}</th>
                                    <th>{t('referral.referrals')}</th>
                                    <th>{t('common.actions')}</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredSellers.map(seller => (
                                    <tr key={seller._id}>
                                        <td>
                                            <strong>{seller.username}</strong>
                                            {seller.isAdmin && (
                                                <Badge bg="warning" className="ms-2" pill>{t('common.admin')}</Badge>
                                            )}
                                        </td>
                                        <td>{formatDate(seller.createdAt)}</td>
                                        <td>{formatDate(seller.lastLogin)}</td>
                                        <td>{seller.referralLinksCount || 0}</td>
                                        <td>{seller.referralsCount || 0}</td>
                                        <td>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleSetSellerRole(seller._id, false)}
                                                disabled={processing === seller._id}
                                            >
                                                {processing === seller._id ? (
                                                    <Spinner animation="border" size="sm" />
                                                ) : (
                                                    <>
                                                        <PersonDash className="me-1" />
                                                        {t('admin.revokeSeller')}
                                                    </>
                                                )}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            {search ? (
                                <p>{t('referral.noSearchResults')}</p>
                            ) : (
                                <p>{t('admin.noSellers')}</p>
                            )}
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Card className="glass-card">
                <Card.Header>
                    <h5 className="mb-0">{t('admin.assignSellerRole')}</h5>
                </Card.Header>
                <Card.Body>
                    <div className="table-responsive">
                        <Table hover className="web3-table">
                            <thead>
                            <tr>
                                <th>{t('common.user')}</th>
                                <th>{t('referral.registrationDate')}</th>
                                <th>{t('referral.lastLogin')}</th>
                                <th>{t('dashboard.flightSheets')}</th>
                                <th>{t('common.actions')}</th>
                            </tr>
                            </thead>
                            <tbody>
                            {users.slice(0, 10).map(user => (
                                <tr key={user._id}>
                                    <td>
                                        <strong>{user.username}</strong>
                                        {user.isAdmin && (
                                            <Badge bg="warning" className="ms-2" pill>{t('common.admin')}</Badge>
                                        )}
                                    </td>
                                    <td>{formatDate(user.createdAt)}</td>
                                    <td>{formatDate(user.lastLogin)}</td>
                                    <td>{user.flightSheetsCount || 0}</td>
                                    <td>
                                        <Button
                                            variant="outline-success"
                                            size="sm"
                                            onClick={() => handlePromoteClick(user)}
                                            disabled={processing === user._id}
                                        >
                                            {processing === user._id ? (
                                                <Spinner animation="border" size="sm" />
                                            ) : (
                                                <>
                                                    <PersonCheck className="me-1" />
                                                    {t('admin.makeSeller')}
                                                </>
                                            )}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    </div>
                    {users.length > 10 && (
                        <div className="text-center mt-3">
                            <p className="text-muted">{t('admin.showingFirst', { count: 10 })}</p>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Модальное окно подтверждения назначения селлера */}
            <Modal show={showPromoteModal} onHide={() => setShowPromoteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('admin.confirmMakeSeller')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedUser && (
                        <p>
                            {t('admin.makeSellerConfirm', { username: selectedUser.username })}
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowPromoteModal(false)}>
                        {t('common.cancel')}
                    </Button>
                    {selectedUser && (
                        <Button
                            variant="success"
                            onClick={() => handleSetSellerRole(selectedUser._id, true)}
                            disabled={processing === selectedUser._id}
                        >
                            {processing === selectedUser._id ? (
                                <Spinner animation="border" size="sm" className="me-2" />
                            ) : null}
                            {t('admin.confirm')}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AdminSellers;